import { Theme } from '@material-ui/core';

import { generateSemanticColorStyles } from '../utils';
import { linkClassName } from './Link';

export const tiptapBlockMarginTop = 'var(--encore-spacing-tighter-2)';
export const tiptapBlockMarginBottom =
  'var(--tiptap-block-margin-bottom, var(--encore-spacing-looser))';
export const tiptapTextPadding = 'var(--encore-spacing-tighter-4) 0';

export const getEditorStyles = (theme: Theme) => ({
  position: 'relative' as const,
  maxWidth: '92ch',
  ...generateSemanticColorStyles(theme.palette.type === 'dark'),
  '& > strong': {
    fontWeight: '700',
  },
  '& > .ProseMirror': {
    outline: 'none',
  },
  '& .ProseMirror-dropcursor': {
    border: 'none',
  },
  '& .editor-paragraph': {
    fontFamily: 'var(--encore-body-font-stack)',
    fontSize: 'var(--encore-text-size-base)',
    // BUI has no line-height tokens; 1.5 is standard for readable body text
    lineHeight: 1.5,
    color: 'var(--text-base)',
    padding: tiptapTextPadding,
    margin: 0,
    cursor: 'text',
  },
  '& .editor-paragraph.is-empty::before': {
    color: 'var(--essential-subdued)',
    content: 'attr(data-placeholder)',
    float: 'left' as const,
    height: 0,
    pointerEvents: 'none' as const,
  },
  '& .caption': {
    fontFamily: 'var(--encore-body-font-stack)',
    fontSize: 'var(--encore-text-size-smaller-2)',
    lineHeight: 1.4,
    color: 'var(--text-subdued)',
    fontWeight: 700,
    letterSpacing: '0.5px',
    margin: 0,
    padding: tiptapTextPadding,
    cursor: 'text',
  },
  '& p.is-editor-empty::before': {
    color: 'var(--essential-subdued)',
    content: 'attr(data-placeholder)',
    float: 'left' as const,
    height: 0,
    pointerEvents: 'none' as const,
  },
  [`& a.${linkClassName}`]: {
    color: 'var(--text-bright-accent)',
    textDecoration: 'underline',
    textDecorationThickness: '0.05em',
    textUnderlineOffset: '20%',
    cursor: 'pointer',
    '&:hover': {
      color: 'var(--text-bright-accent)',
    },
  },
  '& :not(pre) > code': {
    backgroundColor: 'var(--background-base)',
    border: '1px solid var(--decorative-subdued)',
    borderRadius: 'var(--encore-corner-radius-base)',
    color: 'var(--text-base)',
    fontFamily: 'var(--encore-bodyMono-font-stack, monospace)',
    fontSize: 'var(--encore-text-size-smaller-2)',
    padding: '0 var(--encore-spacing-tighter-4)',
  },
  '& [data-type="callout-block"]': {
    marginTop: tiptapBlockMarginTop,
    marginBottom: tiptapBlockMarginBottom,
  },
  '& [data-type="callout-block"] .editor-paragraph': {
    padding: 0,
  },
  '& .editor-heading': {
    margin: 0,
    padding: tiptapTextPadding,
  },
  '& .editor-heading-4': {
    fontSize: 'var(--encore-text-size-base)',
    marginTop: 'var(--encore-spacing-tighter)',
  },
  '& .editor-heading-3': {
    fontSize: 'var(--encore-text-size-large)',
    marginTop: 'var(--encore-spacing-base)',
  },
  '& .editor-heading-2': {
    fontSize: 'var(--encore-text-size-larger)',
    marginTop: 'var(--encore-spacing-looser)',
  },
  '& .editor-heading-1': {
    fontSize: 'var(--encore-text-size-larger-2)',
    marginTop: 'var(--encore-spacing-looser-2)',
  },
  '& > .ProseMirror > .editor-heading:first-child': {
    marginTop: 0,
  },
  '& ul.editor-bullet-list, & ol.editor-ordered-list': {
    margin: 'var(--encore-spacing-tighter-2) 0',
    paddingLeft: 'var(--encore-spacing-tighter-4)',
    fontSize: 'var(--encore-text-size-smaller)',
  },
  '& ul.editor-bullet-list': {
    listStyleType: 'none',
    marginBottom: tiptapBlockMarginBottom,
    '& > li': {
      alignItems: 'baseline',
      display: 'flex',
      '&::before': {
        backgroundColor: 'var(--essential-bright-accent)',
        borderRadius: '50%',
        content: '""',
        height: 6,
        flexShrink: 0,
        marginRight: '0.5em',
        position: 'relative' as const,
        top: '-1px',
        width: 6,
      },
    },
    '& > li > p': {
      margin: 0,
      padding: 'var(--encore-spacing-tighter-4)',
    },
  },
  '& ol.editor-ordered-list': {
    paddingLeft: 'var(--encore-spacing-looser)',
    '& > li > p': {
      margin: 0,
      padding: tiptapTextPadding,
    },
  },
  '& hr': {
    border: 'none',
    borderTop: '1px solid var(--decorative-subdued)',
    margin: 'var(--encore-spacing-base) 0',
  },
  '& .editor-columns': {
    display: 'flex',
    margin: 0,
    marginBottom: tiptapBlockMarginBottom,
    marginTop: tiptapBlockMarginTop,
    width: '100%',
  },
  '& .editor-column': {
    flex: '1 1 0%',
    minWidth: 0,
    margin: 0,
    borderRight: '1px solid transparent',
    paddingRight: 'var(--encore-spacing-tighter-2)',
    paddingLeft: 'var(--encore-spacing-tighter-2)',
    transition: 'border-color 0.1s ease-in-out',
    '& .editor-paragraph': {
      position: 'relative' as const,
    },
    '& .editor-paragraph.is-empty::before': {
      float: 'none' as const,
      height: 'auto',
      position: 'absolute' as const,
      left: 0,
      right: 0,
      whiteSpace: 'nowrap' as const,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
    '&:first-child': {
      paddingLeft: 0,
    },
    '&:last-child': {
      borderRight: 'none',
      paddingRight: 0,
    },
    '& > :last-child': {
      '--tiptap-block-margin-bottom': tiptapBlockMarginTop,
    },
  },
  '& .editor-columns:hover .editor-column:not(:last-child)': {
    borderRight: '1px solid var(--decorative-subdued)',
  },
  '& .editor-column--focused:not(:last-child)': {
    borderRight: '1px solid var(--decorative-subdued)',

    '& > *:last-child': {
      marginBottom: 0,
    },
  },
  '& [data-type="callout-block"] ul.editor-bullet-list, & [data-type="callout-block"] ol.editor-ordered-list':
    {
      margin: 0,
      paddingLeft: 0,
    },
  '& table': {
    borderCollapse: 'separate' as const,
    borderSpacing: 0,
    tableLayout: 'fixed' as const,
    width: '100%',
    minWidth: '600px',
    marginBottom: tiptapBlockMarginBottom,
    marginTop: tiptapBlockMarginTop,
    lineHeight: 1.4,
    border: '1px solid var(--decorative-subdued)',
    borderRadius: 'var(--encore-spacing-tighter-4)',

    '& td, & th': {
      minWidth: '100px',
      borderBottom:
        '1px solid color-mix(in srgb, var(--decorative-subdued) 40%, transparent)',
      borderRight:
        '1px solid color-mix(in srgb, var(--decorative-subdued) 40%, transparent)',
      padding:
        'var(--encore-spacing-tighter-2) var(--encore-spacing-tighter-2)',
      verticalAlign: 'top',
      boxSizing: 'border-box' as const,
      position: 'relative' as const,
      color: 'var(--text-base)',

      '&:last-child': {
        borderRight: 'none',
      },

      '& > *': {
        marginBottom: 0,
      },

      '& > p': {
        fontSize: 'var(--encore-text-size-smaller)',
        lineHeight: 1.4,
      },
    },

    '& tr:last-child td': {
      borderBottom: 'none',
    },

    '& th': {
      fontWeight: 'inherit',
      textAlign: 'left' as const,
      borderBottom: '1px solid var(--decorative-subdued)',
    },

    '& .selectedCell:after': {
      zIndex: 2,
      position: 'absolute' as const,
      content: '""',
      left: 0,
      right: 0,
      top: 0,
      bottom: 0,
      backgroundColor: 'var(--background-tinted-press)',
      pointerEvents: 'none' as const,
    },

    '& td .editor-paragraph.is-empty::before, & th .editor-paragraph.is-empty::before':
      {
        float: 'none' as const,
        height: 'auto',
        position: 'absolute' as const,
        left: 'var(--encore-spacing-tighter-2)',
        right: 'var(--encore-spacing-tighter-2)',
        whiteSpace: 'nowrap' as const,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      },

    '& .column-resize-handle': {
      position: 'absolute' as const,
      right: -8,
      top: 0,
      bottom: -2,
      width: 16,
      background: 'transparent',
      cursor: 'col-resize',

      '&::after': {
        content: '""',
        position: 'absolute' as const,
        left: 7,
        top: 0,
        bottom: 0,
        width: 2,
        backgroundColor: 'transparent',
        transition: 'background-color 0.15s',
      },

      '&:hover::after': {
        backgroundColor: 'var(--essential-base)',
      },
    },
  },
  '& .tiptap-comment-highlight': {
    backgroundColor: 'rgba(255, 223, 0, 0.3)',
    cursor: 'pointer',
    borderBottom: '2px solid var(--essential-warning)',
    '&:hover': {
      backgroundColor: 'rgba(255, 223, 0, 0.5)',
    },
  },
});
